import {
  ShopifyCollectionProductsSortParamsType,
  ShopifyProductsQuerySortParamsType,
} from './shopifyGraphql/products';
import {
  InputOptionsType,
  NavigationType,
  ProductCategory,
  ValueOf,
  MetadataType,
} from './types';

export const TIME_ZONE = 'Australia/Sydney';

export const TWL_SHOP = 'The Whisky List Shop';

export const IndexPageViewType = Object.freeze({
  grid: 'grid',
  list: 'list',
});

export const productGroups = {
  newArrivals: {
    value: 'new-arrivals',
    label: 'New Arrivals',
  },
  twlRecommended: {
    value: 'twl-recommended',
    label: 'TWL Recommended',
  },
  popular: {
    value: 'popular',
    label: 'Popular',
  },
  trending: {
    value: 'trending',
    label: 'Trending',
  },
  recentlyUpdated: {
    value: 'recently-updated',
    label: 'Recently Updated',
  },
  highestRated: {
    value: 'highest-rated',
    label: 'Highest Rated',
  },
  onSale: {
    value: 'on-sale',
    label: 'On Sale',
  },
};

export const SocialMediaHref = Object.freeze({
  facebook: 'https://www.facebook.com/TheWhiskyList/',
  twitter: 'https://twitter.com/thewhiskylist',
  instagram: 'https://www.instagram.com/thewhiskylist/',
  wlaFacebook: 'https://www.facebook.com/groups/wla30',
  email: 'mailto:cheers@thewhiskylist.com.au',
});

export const REWARDS_IB_TAG = 'TWL IB';
export const REWARDS_IB_DISCOUNT_PERCENTAGE = 0.2;

export const filterRanges = {
  age: {min: 0, max: 80},
  abv: {min: 0, max: 100},
};

export const filterByPriceOptions = [
  {
    label: 'Under $75',
    value: 'under75',
    range: {max: 75},
  },
  {
    label: '$75 - $125',
    value: '75-125',
    range: {min: 75, max: 125},
  },
  {
    label: '$125 - $175',
    value: '125-175',
    range: {min: 125, max: 175},
  },
  {
    label: '$175 - $225',
    value: '175-225',
    range: {min: 175, max: 225},
  },
  {
    label: '$225 or more',
    value: 'over225',
    range: {min: 225},
  },
];

export const filterInStockOptions = Object.freeze({
  any: {
    label: null,
    value: 'any',
  },
  exclusive: {
    label: 'TWL Exclusive',
    value: 'twl-exclusive',
  },
  shop: {
    label: 'TWL Shop',
    value: 'twl-shop',
  },
  marketplace: {
    label: 'Marketplace Products',
    value: 'marketplace',
  },
  inStock: {
    label: 'All In-Stock Products',
    value: '',
  },
});

export const availableForSaleOptions = Object.freeze({
  partnerOnly: 'partner-only',
  nonPartnerOnly: 'non-partner-only',
  partnerOrNonPartner: 'partner-or-non-partner',
});

export const filterByProductGroupOptions: InputOptionsType = Object.freeze({
  default: productGroups.newArrivals,
  twlRecommended: productGroups.twlRecommended,
  popular: productGroups.popular,
  trending: productGroups.trending,
  recentlyUpdated: productGroups.recentlyUpdated,
  highestRated: productGroups.highestRated,
});

export const filterOtherSpiritsByProductGroupOptions: InputOptionsType =
  Object.freeze({
    default: productGroups.newArrivals,
    popular: productGroups.popular,
    trending: productGroups.trending,
    recentlyUpdated: productGroups.recentlyUpdated,
  });

export const filterMemberProductOptions: {
  [key: string]: {value: string; label: string; filterValue: string};
} = Object.freeze({
  default: {
    value: '',
    filterValue: '',
    label: 'All',
  },
  whisky: {
    ...ProductCategory.whisky,
    filterValue: ProductCategory.whisky.value,
  },
  gin: {...ProductCategory.gin, filterValue: ProductCategory.gin.value},
  other: {
    filterValue: Object.values(ProductCategory)
      .map(({value}) => value)
      .filter(
        (cat) =>
          ![ProductCategory.whisky.value, ProductCategory.gin.value].includes(
            cat,
          ),
      )
      .join(','),
    label: 'Other',
    value: 'other',
  },
});

export const filterMemberProductByRatingOptions: {
  [key: string]: {value: string; label: string; filterValue: string};
} = Object.freeze({
  default: {
    value: '',
    filterValue: '',
    label: 'All',
  },
  ...(() => {
    const ratings = {};
    [1, 2, 3, 4, 5].forEach((rating) => {
      ratings[rating] = {
        value: `${rating}`,
        filterValue: `${rating}`,
        label: {
          1: 'Dislike (1)',
          2: 'OK (2)',
          3: 'Good (3)',
          4: 'Great (4)',
          5: 'Love (5)',
        }[rating],
      };
    });
    return ratings;
  })(),
});

export const defaultNavigation: NavigationType = Object.freeze({
  site_top_alert: {
    is_enabled: false,
    is_homepage_only: true,
    content: '',
  },
  main_navigation: [
    {
      columns: [
        {
          uuid: '1',
          column_position: 'column-1',
          column_heading: 'Shop',
          navigation_links: [
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Shop',
              link_tag: null,
              link: '/shop',
              link_target: '_blank',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Single Malt',
              link_tag: null,
              link: '/shop/?types=Single+Malt',
              link_target: '_blank',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Bourbon',
              link_tag: null,
              link: '/shop/?types=Bourbon',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
        {
          uuid: '2',
          column_position: 'column-2',
          column_heading: 'Whisky by country',
          navigation_links: [
            {
              link_type: 'country',
              country_icon: 'ireland',
              link_label: 'Ireland',
              link_tag: null,
              link: '/shop/?countries=ireland',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'japan',
              link_label: 'Japan',
              link_tag: null,
              link: '/shop/?countries=japan',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'taiwan',
              link_label: 'Taiwan',
              link_tag: null,
              link: '/shop/?countries=taiwan',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'scotland',
              link_label: 'Scotland',
              link_tag: null,
              link: '/shop/?countries=scotland',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'australia',
              link_label: 'Australia',
              link_tag: null,
              link: '/shop/?countries=australia',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'united-states',
              link_label: 'United States',
              link_tag: null,
              link: '/shop/?countries=united-states',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'rest',
              link_label: 'Rest of the world',
              link_tag: null,
              link: '/shop?countries=rest-of-the-world',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: 'india',
              link_label: 'India',
              link_tag: null,
              link: '/shop?countries=india',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
      ],
      category_name: 'whisky',
      featured_link1_image:
        'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/36519_0_tn.jpeg',
      featured_link1_text: 'Browse Highest rated Whiskies of 2021',
      featured_link1_url: '/shop',
      featured_link1_target: '_self',
      featured_link2_image:
        'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/36519_0.jpeg',
      featured_link2_text: 'Browse TWL Exclusive Bottlings',
      featured_link2_url: '/shop',
      featured_link2_target: '_self',
      extra_link_text: 'Browse all whiskies',
      extra_link_url: '/shop',
      extra_link_target: '_self',
    },
    {
      columns: [
        {
          uuid: '1',
          column_position: 'column-1',
          column_heading: 'Events and Text Pages',
          navigation_links: [
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Terms and Conditions',
              link_tag: null,
              link: '/terms',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Virtual Events',
              link_tag: null,
              link: '/events?type=virtual',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Events',
              link_tag: null,
              link: '/events',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Shipping Policy',
              link_tag: null,
              link: '/shipping',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Event Detail Buy Ticket',
              link_tag: null,
              link: '/buy_tricket',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'About Us',
              link_tag: null,
              link: '/about-us',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Privacy Policy',
              link_tag: null,
              link: '/privacy',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
        {
          uuid: '2',
          column_position: 'column-2',
          column_heading: 'News and Bar Pages',
          navigation_links: [
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'News List',
              link_tag: null,
              link: '/news',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Australian Bars & Venues',
              link_tag: null,
              link: '/bars',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
      ],
      category_name: 'events-and-tastings',
      featured_link1_image: null,
      featured_link1_text: '',
      featured_link1_url: '',
      featured_link1_target: '_self',
      featured_link2_image: null,
      featured_link2_text: '',
      featured_link2_url: '',
      featured_link2_target: '_self',
      extra_link_text: '',
      extra_link_url: '',
      extra_link_target: '_self',
    },
    {
      columns: [
        {
          uuid: '1',
          column_type: 'feature_link',
          column_position: 'column-1',
          link: '/shop?category=whisky',
          link_target: '_self',
          link_text: 'Browse',
          background_color: '#1F2641',
          font_color: '#FFFFFF',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/top-whiskies.png',
        },
        {
          uuid: '2',
          column_type: 'feature_link',
          column_position: 'column-3',
          link: '/shop?category=whisky',
          link_target: '_self',
          link_text: 'Browse',
          background_color: '#F73400',
          font_color: '#FFFFFF',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/popular-gins.png',
        },
      ],
      category_name: 'community',
      featured_link1_image: null,
      featured_link1_text: '',
      featured_link1_url: '',
      featured_link1_target: '_self',
      featured_link2_image: null,
      featured_link2_text: '',
      featured_link2_url: '',
      featured_link2_target: '_self',
      extra_link_text: '',
      extra_link_url: '',
      extra_link_target: '_self',
    },
    {
      columns: [
        {
          uuid: '1',
          column_position: 'column-1',
          column_heading: 'Whisky by country',
          navigation_links: [
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Japan',
              link_tag: null,
              link: '/shop?country=japan',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
      ],
      category_name: 'distilleries-and-bars',
      featured_link1_image: null,
      featured_link1_text: '',
      featured_link1_url: '',
      featured_link1_target: '_self',
      featured_link2_image: null,
      featured_link2_text: '',
      featured_link2_url: '',
      featured_link2_target: '_self',
      extra_link_text: '',
      extra_link_url: '',
      extra_link_target: '_self',
    },
    {
      columns: [
        {
          uuid: '1',
          column_type: 'feature_link',
          column_position: 'column-1',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#F73400',
          font_color: '#ffffff',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/popular-gins_p2c94tB.png',
        },
        {
          uuid: '2',
          column_type: 'feature_link',
          column_position: 'column-2',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#1F2641',
          font_color: '#ffffff',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/top-whiskies_t0GwqHJ.png',
        },
        {
          uuid: '3',
          column_type: 'feature_link',
          column_position: 'column-3',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#DADADA',
          font_color: '#ffffff',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/browse-whisky_SIzygSz.png',
        },
        {
          uuid: '4',
          column_type: 'feature_link',
          column_position: 'column-4',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#F73400',
          font_color: '#ffffff',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/popular-gins_LKiJjwq.png',
        },
      ],
      category_name: 'news-and-reviews',
      featured_link1_image: null,
      featured_link1_text: '',
      featured_link1_url: '',
      featured_link1_target: '_self',
      featured_link2_image: null,
      featured_link2_text: '',
      featured_link2_url: '',
      featured_link2_target: '_self',
      extra_link_text: '',
      extra_link_url: '',
      extra_link_target: '_self',
    },
    {
      columns: [
        {
          uuid: '1',
          column_position: 'column-1',
          column_heading: 'Whisky Type',
          navigation_links: [
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Bourbon',
              link_tag: null,
              link: '/Bourbon',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Grain Whisky',
              link_tag: null,
              link: '/grain_whishky',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Single Malt',
              link_tag: null,
              link: '/single_malt',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Independent Bottligs',
              link_tag: null,
              link: '/independent_bottligs',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Blended Whisky',
              link_tag: null,
              link: '/blended_wishky',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Rye Whisky',
              link_tag: null,
              link: '/rye_whisky',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Pot Still',
              link_tag: null,
              link: '/pot_still',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Smokey / Peated Whisky',
              link_tag: null,
              link: '/smokey',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
        {
          uuid: '2',
          column_position: 'column-2',
          column_heading: 'Whisky by country',
          navigation_links: [
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Taiwan',
              link_tag: null,
              link: '/taiwan',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'United States',
              link_tag: null,
              link: '/usa',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Australia',
              link_tag: null,
              link: '/australia',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Japan',
              link_tag: null,
              link: '/japan',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Scotland',
              link_tag: null,
              link: '/scotland',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Ireland',
              link_tag: null,
              link: '/ireland',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'Rest of the world',
              link_tag: null,
              link: '/rest',
              link_target: '_self',
            },
            {
              link_type: 'country',
              country_icon: '',
              link_label: 'India',
              link_tag: null,
              link: '/india',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
        {
          uuid: '3',
          column_position: 'column-3',
          column_heading: 'Whisky Buying Guides',
          navigation_links: [
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 Aussie Whiskies',
              link_tag: null,
              link: '/topwhisky_aussie',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 Whiskies for $50',
              link_tag: null,
              link: '/topwhisky',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 American Whiskies',
              link_tag: null,
              link: '/topwhisky_american',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 Whiskies for $100',
              link_tag: null,
              link: '/topwhisky100',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 luxury Whiskies',
              link_tag: null,
              link: '/topwhisky_luxury',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 Whiskies for $150',
              link_tag: null,
              link: '/topwhisky150',
              link_target: '_self',
            },
            {
              link_type: 'text',
              country_icon: '',
              link_label: 'Top 10 Whiskies for $200',
              link_tag: null,
              link: '/topwhisky200',
              link_target: '_self',
            },
          ],
          column_type: 'link_list',
        },
      ],
      category_name: 'samples-and-gifts',
      featured_link1_image:
        'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/exclusive-bottlings_Kj4ivEs.jpeg',
      featured_link1_text: 'Browse Highest rated Whiskies of 2021',
      featured_link1_url: '/highestrate',
      featured_link1_target: '_self',
      featured_link2_image:
        'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/highest-rated-whiskies_hZAbGLL.jpeg',
      featured_link2_text: 'Browse TWL Exclusive Bottlings',
      featured_link2_url: '/twl_exclusive',
      featured_link2_target: '_self',
      extra_link_text: 'Browse all whiskies',
      extra_link_url: '/all_wishkies',
      extra_link_target: '_self',
    },
    {
      columns: [
        {
          uuid: '1',
          column_type: 'feature_link',
          column_position: 'column-1',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#F73400',
          font_color: '#ffffff',
          title: 'Whisky buying guideTop 10 Whiskies for $100',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/popular-gins_YUvb09o.png',
        },
        {
          uuid: '2',
          column_type: 'feature_link',
          column_position: 'column-2',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#1F2641',
          font_color: '#ffffff',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/top-whiskies_EcWoGLV.png',
        },
        {
          uuid: '3',
          column_type: 'feature_link',
          column_position: 'column-3',
          link: '/topwhisky100',
          link_target: '_self',
          link_text: '',
          background_color: '#DADADA',
          font_color: '#ffffff',
          title: 'Whisky buying guide',
          content_text: '<span>Top 10 Whiskies <br> for $100</span>',
          feature_image:
            'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/browse-whisky_2JQ4VoL.png',
        },
      ],
      category_name: 'other-spirits',
      featured_link1_image:
        'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/exclusive-bottlings_Ln7NHFX.jpeg',
      featured_link1_text: 'Browse Highest rated Whiskies of 2021',
      featured_link1_url: '/highestrate',
      featured_link1_target: '_self',
      featured_link2_image:
        'https://assets.twlstaging.creatio.com.au/media/website_content/navigation/highest-rated-whiskies_o1ga877.jpeg',
      featured_link2_text: 'Browse TWL Exclusive Bottlings',
      featured_link2_url: '/twl_exclusive',
      featured_link2_target: '_self',
      extra_link_text: 'Browse all whiskies',
      extra_link_url: '/all_wishkies',
      extra_link_target: '_self',
    },
  ],
  footer_navigation: [
    {
      column_name: 'Featured',
      footer_navigation_links: [
        {
          link_text: 'Bars',
          link: '/bars',
          link_target: '_self',
        },
        {
          link_text: 'The Whisky Map',
          link: '/thewhiskymap',
          link_target: '_self',
        },
        {
          link_text: 'Reviews',
          link: '/reviews',
          link_target: '_self',
        },
        {
          link_text: 'News',
          link: '/news',
          link_target: '_self',
        },
        {
          link_text: 'Events',
          link: '/events',
          link_target: '_self',
        },
        {
          link_text: 'Featured Brands',
          link: '/featuredbrands',
          link_target: '_self',
        },
        {
          link_text: 'Australian Distilleries',
          link: '/australiandistilleries',
          link_target: '_self',
        },
        {
          link_text: 'The Whisky Hub',
          link: '/whiskyhub',
          link_target: '_self',
        },
      ],
    },
    {
      column_name: 'Spirit Types',
      footer_navigation_links: [
        {
          link_text: 'Whisky',
          link: '/whisky',
          link_target: '_self',
        },
        {
          link_text: 'Gin',
          link: '/gin',
          link_target: '_self',
        },
        {
          link_text: 'Rum',
          link: '/rum',
          link_target: '_self',
        },
      ],
    },
    {
      column_name: 'About',
      footer_navigation_links: [
        {
          link_text: 'Shipping Policy',
          link: '/shipping',
          link_target: '_self',
        },
        {
          link_text: 'Privacy Policy',
          link: '/privacy',
          link_target: '_self',
        },
        {
          link_text: 'Terms Of Service',
          link: '/terms',
          link_target: '_self',
        },
        {
          link_text: 'Partner With Us',
          link: '/partner-with-us',
          link_target: '_self',
        },
        {
          link_text: 'Returns',
          link: '/returns',
          link_target: '_self',
        },
        {
          link_text: 'About Us',
          link: '/about-us',
          link_target: '_self',
        },
        {
          link_text: 'Contact Us',
          link: '/contact-us',
          link_target: '_self',
        },
      ],
    },
  ],
} as NavigationType);

export const defaultWhiskyTypes = [
  'Blend',
  'Blended Malt',
  'Bourbon',
  'Rye',
  'Single Malt',
  'Tennessee Whiskey',
];

export const defaultMetadata: MetadataType = {
  product_categories: [
    'whisky',
    'rum',
    'gin',
    'vodka',
    'brandy',
    'barware',
    'glassware',
    'other-spirits',
    'tequila-and-mezcal',
    'liqueur-and-fortified-wine',
  ],
  communication_channels: [
    {is_default_channel: true, slug: 'sms-or-phone', name: 'SMS / Phone'},
    {is_default_channel: true, slug: 'email', name: 'Email'},
  ],
  product_peat_levels: [
    'light',
    'medium',
    'heavy',
    'super heavy',
    'various',
    'very-light',
  ],
  product_filters: ['chill-filtered', 'non-chill-filtered'],
  product_groups: [
    'best-prices',
    'trending',
    'recently-updated',
    'just-added',
    'highest-rated',
    'twl-favs',
  ],
  product_published_states: ['DRAFT', 'ACTIVE', 'ARCHIVED'],
  product_event_types: [
    'new',
    'price-up',
    'price-down',
    'ran-out-of-stock',
    'restocked',
  ],
  member_whisky_frequencies: [
    'once-a-week',
    'once-a-month',
    'once-in-three-months',
  ],
  member_whisky_budgets: ['under-100', '100-200', 'over-200'],
  flavour_note_types: ['smell', 'taste', 'finish'],
  news_article_categories: [
    'announcement',
    'brand-feature',
    'event',
    'interview',
    'news',
    'twl-review',
  ],
  event_types: ['show', 'live', 'virtual'],
  policy_types: ['privacy', 'terms', 'shipping', 'returns'],
  brands: [
    {value: '100 Pipers', is_default: true},
    {value: '1770', is_default: true},
    {value: '1792 Small Batch', is_default: true},
    {value: '1800 Anejo', is_default: true},
    {value: '23rd Street', is_default: true},
    {value: '5Nines', is_default: true},
    {value: '78 Degrees', is_default: true},
    {value: 'Aberfeldy', is_default: true},
    {value: 'Aberlour', is_default: true},
    {value: 'Acorn', is_default: true},
    {value: 'Adams', is_default: true},
    {value: 'GlenDronach', is_default: true},
    {value: 'Whisky Uncovered', is_default: true},
  ],
  styles: [
    'American Oak',
    'American Oak Cas',
    'American Oak Cask',
    'Blended Whisky',
    'Botanical',
    'Bourbon',
    'Cask Strength',
    'Dark',
    'Dry',
    'Ex Shiraz',
    'Flavoured',
    'Gold',
    'Honey',
    'Honey Whisky',
    'Peated',
    'Rose Floral',
    'Rye',
    'Savoury',
    'Sherry/Wine Cask',
    'Single Malt',
    'Sloe',
    'Spiced',
    'White',
  ],
  types: [
    'Anejo',
    'Barrel Aged',
    'Blend',
    'Blended',
    'Blended Bourbon',
    'Blended Malt',
    'Blended Whiskey Liqueur',
    'Botanical',
    'Bourbon',
    'Citrus',
    'Classic',
    'Corn',
    'Corn Whiskey',
    'Dark',
    'Dark Rum',
    'Davidson Plum',
    'Dry',
    'Flavoured',
    'Flavoured Gin',
    'Floral',
    'Genever',
    'Gift Card',
    'Gin',
    'Gold',
    'Grain',
    'Infused',
    'Liqueur',
    'London Dry',
    'London Dry Gin',
    'Moonshine',
    'Navy Strength',
    'New Make',
    'Organic Gin',
    'Pink',
    'Rum',
    'Rye',
    'Single Blended',
    'Single Column Still',
    'Single Grain',
    'Single Malt',
    'Single Pot Still',
    'Spiced',
    'Tennessee Bourbon',
    'Vermouth',
    'Whey',
    'Whisky Liqueur',
    'White',
  ],
  partner_stores: [
    'Cambridge Cellars',
    'Destination Cellars',
    'Distinct Whiskies',
    'Harvest Liquor',
    'Moreish Wines',
    'MyWhiskyJourneys',
    'Oak Barrel',
    'Old Richmond Cellars',
    'Orrsum Spirits',
    "Paul's Liquor",
    'Select Scotch Whisky',
    'Select Spirits',
    'Single Malt Whisky',
    'The Drink Society',
    'The Dusty Bottle',
    'The Whisky List Shop',
    'The Wine Providore',
    'Theka',
    'Uisuki',
    'United Cellars',
    'Uptown Liquor',
    'Whisky Direct',
  ],
  state_options: [],
};

export const months = new Map<string, string>([
  ['01', 'January'],
  ['02', 'February'],
  ['03', 'March'],
  ['04', 'April'],
  ['05', 'May'],
  ['06', 'June'],
  ['07', 'July'],
  ['08', 'August'],
  ['09', 'September'],
  ['10', 'October'],
  ['11', 'November'],
  ['12', 'December'],
]);

export const monthsArray = Array.from(months.entries());

export const PAGINATION_PARAMS = {
  cursor: {
    before: 'before',
    after: 'after',
  },
  page: {
    page: 'page',
  },
};

export const SHOP_ORDER_OPTIONS = new Map<
  string,
  {
    queryValue: string;
    label: string;
    search: ShopifyProductsQuerySortParamsType;
  }
>([
  [
    'default',
    {
      queryValue: '',
      label: 'Sort by: Default',
      search: {sortKey: 'BEST_SELLING', reverse: null},
    },
  ],
  [
    '-price',
    {
      queryValue: '-price',
      label: 'Price High to Low',
      search: {sortKey: 'PRICE', reverse: true},
    },
  ],
  [
    'price',
    {
      queryValue: 'price',
      label: 'Price Low to High',
      search: {sortKey: 'PRICE', reverse: null},
    },
  ],
  [
    'newest',
    {
      queryValue: 'newest',
      label: 'Newest First',
      search: {sortKey: 'CREATED_AT', reverse: true},
    },
  ],
]);

export const COLLECTION_ORDER_OPTIONS = new Map<
  string,
  {
    queryValue: string;
    label: string;
    search: ShopifyCollectionProductsSortParamsType;
  }
>([
  [
    'default',
    {
      queryValue: '',
      label: 'Sort by: Default',
      search: {sortKey: 'BEST_SELLING', reverse: null},
    },
  ],
  [
    '-price',
    {
      queryValue: '-price',
      label: 'Price High to Low',
      search: {sortKey: 'PRICE', reverse: true},
    },
  ],
  [
    'price',
    {
      queryValue: 'price',
      label: 'Price Low to High',
      search: {sortKey: 'PRICE', reverse: null},
    },
  ],
  [
    'newest',
    {
      queryValue: 'newest',
      label: 'Newest First',
      search: {sortKey: 'CREATED', reverse: true},
    },
  ],
]);

export const reviewRatingFilterOptions = [
  {value: '5', label: 'Rating 5'},
  {value: '4', label: 'Rating 4'},
  {value: '3', label: 'Rating 3'},
  {value: '2', label: 'Rating 2'},
  {value: '1', label: 'Rating 1'},
];

export enum TwlAnalyticsEvent {
  LIVE_SEARCH = 'twl-live-search',
  SHOP_SEARCH = 'twl-shop-search',
  VIEWED_PRODUCT = 'twl-viewed-product',
  ADD_TO_WATCHLIST = 'twl-add-to-watchlist',
  REMOVE_FROM_WATCHLIST = 'twl-remove-from-watchlist',
  ADD_TO_COLLECTION = 'twl-add-to-collection',
  REMOVE_FROM_COLLECTION = 'twl-remove-from-collection',
  RATE_PRODUCT = 'twl-rate-product',
}

export enum VisitorActivityCategory {
  MEMBER_DASHBOARD = 'member-dashboard',
  PRODUCT = 'product',
  SEARCH = 'search',
  PAGE_VIEW = 'page-view',
}

export enum VisitorActivityAction {
  TOGGLE_WATCH_TRUE = 'toggle-watch-true',
  TOGGLE_WATCH_FALSE = 'toggle-watch-false',
  ADD_TO_COLLECTION = 'add-to-collection',
  REMOVE_FROM_COLLECTION = 'remove-from-collection',
  ADD_TO_CART = 'add-to-cart',
  REMOVE_FROM_CART = 'remove-from-cart',
  SEARCH_QUERY = 'search-query',
  SEARCH_QUERY_RESULT = 'search-query-result',
  BROWSE = 'browse',
  ADD_RATING = 'add-rating',
}

export const ModalName = Object.freeze({
  login: 'login',
  cart: 'cart',
  rating: 'rating',
  productReview: 'productReview',
  reviewProduct: 'reviewProduct',
  selectProduct: 'selectProduct',
  editReview: 'editReview',
  inviteFriends: 'inviteFriends',
  signup: 'signup',
  resetPassword: 'resetPassword',
  closeAccount: 'closeAccount',
  selectModal: 'selectModal',
  simpleInfo: 'simpleInfo',
  howToEarnPoints: 'howToEarnPoints',
  howToWinMonthlyGiveaway: 'howToWinMonthlyGiveaway',
  howToWinFreeWhisky: 'howToWinFreeWhisky',
  requestProduct: 'requestProduct',
  editMemberCollectedProduct: 'editMemberCollectedProduct',
  productMarketplacePricing: 'productMarketplacePricing',
});

export const EventType = Object.freeze({
  show: 'show',
  live: 'live',
  virtual: 'virtual',
  whiskyMonth: 'whisky-month',
});

export type ModalNameType = ValueOf<typeof ModalName>;

export const VARIANT_DEFAULT_TITLE = 'Default Title';

export const SEO_DEFAULT_DESCRIPTION =
  "Australia's largest online whisky store, exclusive bottlings and " +
  "Whisky Lover's perks like epic whisky deals, tastings, new releases " +
  'and more - free to join.';

export const SEO_DEFAULT_TITLE =
  "The Whisky List | Australia's Online Home For Whisky";

export const MyEventsListType = Object.freeze({
  upcoming: 'upcoming',
  past: 'past',
});

export const CountryCodes: InputOptionsType = Object.freeze(
  Object.fromEntries(
    Object.entries({
      AU: 'Australia',
      NZ: 'New Zealand',
      AC: 'Ascension Island',
      AD: 'Andorra',
      AE: 'United Arab Emirates',
      AF: 'Afghanistan',
      AG: 'Antigua & Barbuda',
      AI: 'Anguilla',
      AL: 'Albania',
      AM: 'Armenia',
      AN: 'Netherlands Antilles',
      AO: 'Angola',
      AR: 'Argentina',
      AT: 'Austria',
      AW: 'Aruba',
      AX: 'Åland Islands',
      AZ: 'Azerbaijan',
      BA: 'Bosnia & Herzegovina',
      BB: 'Barbados',
      BD: 'Bangladesh',
      BE: 'Belgium',
      BF: 'Burkina Faso',
      BG: 'Bulgaria',
      BH: 'Bahrain',
      BI: 'Burundi',
      BJ: 'Benin',
      BL: 'St. Barthélemy',
      BM: 'Bermuda',
      BN: 'Brunei',
      BO: 'Bolivia',
      BQ: 'Caribbean Netherlands',
      BR: 'Brazil',
      BS: 'Bahamas',
      BT: 'Bhutan',
      BV: 'Bouvet Island',
      BW: 'Botswana',
      BY: 'Belarus',
      BZ: 'Belize',
      CA: 'Canada',
      CC: 'Cocos (Keeling) Islands',
      CD: 'Congo - Kinshasa',
      CF: 'Central African Republic',
      CG: 'Congo - Brazzaville',
      CH: 'Switzerland',
      CI: 'Côte d’Ivoire',
      CK: 'Cook Islands',
      CL: 'Chile',
      CM: 'Cameroon',
      CN: 'China',
      CO: 'Colombia',
      CR: 'Costa Rica',
      CU: 'Cuba',
      CV: 'Cape Verde',
      CW: 'Curaçao',
      CX: 'Christmas Island',
      CY: 'Cyprus',
      CZ: 'Czechia',
      DE: 'Germany',
      DJ: 'Djibouti',
      DK: 'Denmark',
      DM: 'Dominica',
      DO: 'Dominican Republic',
      DZ: 'Algeria',
      EC: 'Ecuador',
      EE: 'Estonia',
      EG: 'Egypt',
      EH: 'Western Sahara',
      ER: 'Eritrea',
      ES: 'Spain',
      ET: 'Ethiopia',
      FI: 'Finland',
      FJ: 'Fiji',
      FK: 'Falkland Islands',
      FO: 'Faroe Islands',
      FR: 'France',
      GA: 'Gabon',
      GB: 'United Kingdom',
      GD: 'Grenada',
      GE: 'Georgia',
      GF: 'French Guiana',
      GG: 'Guernsey',
      GH: 'Ghana',
      GI: 'Gibraltar',
      GL: 'Greenland',
      GM: 'Gambia',
      GN: 'Guinea',
      GP: 'Guadeloupe',
      GQ: 'Equatorial Guinea',
      GR: 'Greece',
      GS: 'South Georgia & South Sandwich Islands',
      GT: 'Guatemala',
      GW: 'Guinea-Bissau',
      GY: 'Guyana',
      HK: 'Hong Kong SAR',
      HM: 'Heard & McDonald Islands',
      HN: 'Honduras',
      HR: 'Croatia',
      HT: 'Haiti',
      HU: 'Hungary',
      ID: 'Indonesia',
      IE: 'Ireland',
      IL: 'Israel',
      IM: 'Isle of Man',
      IN: 'India',
      IO: 'British Indian Ocean Territory',
      IQ: 'Iraq',
      IR: 'Iran',
      IS: 'Iceland',
      IT: 'Italy',
      JE: 'Jersey',
      JM: 'Jamaica',
      JO: 'Jordan',
      JP: 'Japan',
      KE: 'Kenya',
      KG: 'Kyrgyzstan',
      KH: 'Cambodia',
      KI: 'Kiribati',
      KM: 'Comoros',
      KN: 'St. Kitts & Nevis',
      KP: 'North Korea',
      KR: 'South Korea',
      KW: 'Kuwait',
      KY: 'Cayman Islands',
      KZ: 'Kazakhstan',
      LA: 'Laos',
      LB: 'Lebanon',
      LC: 'St. Lucia',
      LI: 'Liechtenstein',
      LK: 'Sri Lanka',
      LR: 'Liberia',
      LS: 'Lesotho',
      LT: 'Lithuania',
      LU: 'Luxembourg',
      LV: 'Latvia',
      LY: 'Libya',
      MA: 'Morocco',
      MC: 'Monaco',
      MD: 'Moldova',
      ME: 'Montenegro',
      MF: 'St. Martin',
      MG: 'Madagascar',
      MK: 'North Macedonia',
      ML: 'Mali',
      MM: 'Myanmar (Burma)',
      MN: 'Mongolia',
      MO: 'Macao SAR',
      MQ: 'Martinique',
      MR: 'Mauritania',
      MS: 'Montserrat',
      MT: 'Malta',
      MU: 'Mauritius',
      MV: 'Maldives',
      MW: 'Malawi',
      MX: 'Mexico',
      MY: 'Malaysia',
      MZ: 'Mozambique',
      NA: 'Namibia',
      NC: 'New Caledonia',
      NE: 'Niger',
      NF: 'Norfolk Island',
      NG: 'Nigeria',
      NI: 'Nicaragua',
      NL: 'Netherlands',
      NO: 'Norway',
      NP: 'Nepal',
      NR: 'Nauru',
      NU: 'Niue',
      OM: 'Oman',
      PA: 'Panama',
      PE: 'Peru',
      PF: 'French Polynesia',
      PG: 'Papua New Guinea',
      PH: 'Philippines',
      PK: 'Pakistan',
      PL: 'Poland',
      PM: 'St. Pierre & Miquelon',
      PN: 'Pitcairn Islands',
      PS: 'Palestinian Territories',
      PT: 'Portugal',
      PY: 'Paraguay',
      QA: 'Qatar',
      RE: 'Réunion',
      RO: 'Romania',
      RS: 'Serbia',
      RU: 'Russia',
      RW: 'Rwanda',
      SA: 'Saudi Arabia',
      SB: 'Solomon Islands',
      SC: 'Seychelles',
      SD: 'Sudan',
      SE: 'Sweden',
      SG: 'Singapore',
      SH: 'St. Helena',
      SI: 'Slovenia',
      SJ: 'Svalbard & Jan Mayen',
      SK: 'Slovakia',
      SL: 'Sierra Leone',
      SM: 'San Marino',
      SN: 'Senegal',
      SO: 'Somalia',
      SR: 'Suriname',
      SS: 'South Sudan',
      ST: 'São Tomé & Príncipe',
      SV: 'El Salvador',
      SX: 'Sint Maarten',
      SY: 'Syria',
      SZ: 'Eswatini',
      TA: 'Tristan da Cunha',
      TC: 'Turks & Caicos Islands',
      TD: 'Chad',
      TF: 'French Southern Territories',
      TG: 'Togo',
      TH: 'Thailand',
      TJ: 'Tajikistan',
      TK: 'Tokelau',
      TL: 'Timor-Leste',
      TM: 'Turkmenistan',
      TN: 'Tunisia',
      TO: 'Tonga',
      TR: 'Türkiye',
      TT: 'Trinidad & Tobago',
      TV: 'Tuvalu',
      TW: 'Taiwan',
      TZ: 'Tanzania',
      UA: 'Ukraine',
      UG: 'Uganda',
      UM: 'U.S. Outlying Islands',
      US: 'United States',
      UY: 'Uruguay',
      UZ: 'Uzbekistan',
      VA: 'Vatican City',
      VC: 'St. Vincent & Grenadines',
      VE: 'Venezuela',
      VG: 'British Virgin Islands',
      VN: 'Vietnam',
      VU: 'Vanuatu',
      WF: 'Wallis & Futuna',
      WS: 'Samoa',
      XK: 'Kosovo',
      YE: 'Yemen',
      YT: 'Mayotte',
      ZA: 'South Africa',
      ZM: 'Zambia',
      ZW: 'Zimbabwe',
    }).map(([key, value]) => [key, {value: key, label: value}]),
  ),
);
